import React, { useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import type { IImage } from 'tbk-components/src/components/Image';
import { ILink } from 'tbk-components/src/components/Button';
export interface ITablesHeadings {
	/**
	 *  Heading Label
	 */
	label?: string;
}
export interface ITablesItems {
	/**
	 *  Item Label
	 * @control richtext
	 */
	label?: string;
	/**
	 *  Item Type
	 */
	itemType?: 'file' | 'text' | 'link' | 'empty';
	/**
	 *  Item File
	 */
	file?: IImage;
	/**
	 *  Item Link
	 */
	link?: ILink;
}
export interface ITablesColumns {
	/**
	 * Column Label (not displayed on site)
	 */
	label?: string;
	/**
	 * Columns Items
	 */
	items?: ITablesItems[];
}
export interface ITablesRows {
	/**
	 * Row Heading
	 */
	heading?: string;
	/**
	 * Row Column
	 */
	columns?: ITablesColumns[];
}
export interface ITablesProps extends IBaseElementProps {
	/**
	 * Table Headings
	 */
	headings?: ITablesHeadings[];
	/**
	 * Table Column Alignment
	 */
	columnAlignment?: 'Default' | 'Left';
	/**
	 * Table Columns Visible (On Desktop)
	 */
	columnsVisible?: number;
	/**
	 * Table Rows
	 */
	rows?: ITablesRows[];
	/**
	 * Table Row Heading Width
	 */
	rowHeadingWidth?: number;
	/**
	 * Table Footnotes
	 */
	footnote?: string;
	/**
	 * @control richtext
	 */
}

/**
 * Tables - If you find this block difficult to work with, consider using the native WordPress "Table" block instead.
 * @block
 * @icon list-view
 */
const Tables: React.FC<ITablesProps> = ({
	headings,
	columnAlignment,
	rows,
	rowHeadingWidth,
	columnsVisible,
	footnote,
	id,
	className,
	classNames = [],
}) => {
	const [columnWidth, setColumnWidth] = useState<number | null>(0);
	const [containerWidth, setContainerWidth] = useState<number | null>(0);
	const [tableWidth, setTableWidth] = useState<number | null>(0);
	const [containerScroll, setContainerScroll] = useState<number | null>(0);

	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!ref.current) {
			return;
		}

		const resetScroll = () => {
			const container = ref.current?.querySelector(
				'.calian-tables__container',
			) as HTMLElement;
			container.scrollLeft = 0;
			setContainerScroll(0);
			changeColumnWidth();
		};

		const changeColumnWidth = () => {
			const container = ref.current?.querySelector(
				'.calian-tables__container',
			) as HTMLElement;

			const table = ref.current?.querySelector(
				'.calian-tables__table',
			) as HTMLElement;

			const visibleColumns =
				columnsVisible && columnsVisible > 3 ? columnsVisible - 1 : 2;
			const hiddenColumns = headings?.length - visibleColumns - 1;
			const headingWidth = rowHeadingWidth ? rowHeadingWidth : 336;
			const width =
				(container.offsetWidth - headingWidth) / visibleColumns;
			const tableWidth =
				window.innerWidth < 767
					? table.offsetWidth
					: container.offsetWidth + width * hiddenColumns;

			setColumnWidth(width);
			setTableWidth(tableWidth);
			setContainerWidth(container.offsetWidth);

			const columns = ref.current?.querySelectorAll(
				'.calian-tables__table-row-heading--is-not-first-heading, .calian-tables__table-row-column',
			) as NodeListOf<HTMLElement>;

			columns.forEach((column) => {
				column.style.minWidth = `${width}px`;
				column.style.width = `${width}px`;
			});

			if (rowHeadingWidth) {
				const headings = ref.current?.querySelectorAll(
					'.calian-tables__table-heading--is-first-heading, .calian-tables__mask, .calian-tables__table-row-heading',
				) as NodeListOf<HTMLElement>;

				headings.forEach((heading) => {
					heading.style.minWidth = `${rowHeadingWidth}px`;
					heading.style.width = `${rowHeadingWidth}px`;
				});
			}
		};
		changeColumnWidth();
		window.addEventListener('load', resetScroll);
		window.addEventListener('resize', resetScroll);

		return () => {
			window.removeEventListener('load', resetScroll);
			window.removeEventListener('resize', resetScroll);
		};
	}, []);

	const scrollPrev = () => {
		const mobileColumnWidth = containerWidth / 2;
		const width = window.innerWidth < 767 ? mobileColumnWidth : columnWidth;
		const container = ref.current?.querySelector(
			'.calian-tables__container',
		) as HTMLElement;
		container.scrollLeft -= width;
		setContainerScroll(container.scrollLeft - width);
	};

	const scrollNext = () => {
		const mobileColumnWidth = containerWidth / 2;
		const width = window.innerWidth < 767 ? mobileColumnWidth : columnWidth;
		const container = ref.current?.querySelector(
			'.calian-tables__container',
		) as HTMLElement;
		container.scrollLeft += width;
		setContainerScroll(container.scrollLeft + width);
	};

	return (
		<div
			id={id}
			className={className || cn(
				'calian-tables',
				headings?.length <= 2 && 'calian-tables--two-column',
				columnsVisible &&
					columnsVisible > 3 &&
					'calian-tables--many-column',
				headings?.length === 3 && 'calian-tables--hide-controls',
				columnsVisible &&
					headings?.length <= columnsVisible &&
					'calian-tables--hide-controls',
				columnAlignment &&
					columnAlignment === 'Left' &&
					'calian-tables--align-left',
				...classNames,
			)}
			ref={ref}
		>
			<div className="calian-tables__controls">
				<div className="calian-tables__mask">
					{
						(headings || []).map(({ label }, index) => (
							<div key={index}>{label}</div>
						))[0]
					}
				</div>
				<button
					type="button"
					aria-label="Previous"
					className={cn(
						'calian-tables__prev ',
						containerScroll === 0 &&
							'calian-tables__prev--is-hidden',
					)}
					onClick={() => {
						scrollPrev();
					}}
					style={
						rowHeadingWidth
							? {
									left: `${rowHeadingWidth}px`,
							  }
							: {
									left: `352px`,
							  }
					}
				>
					<span className="calian-tables__icon icon icon-caret-left-slim"></span>
				</button>
				<button
					type="button"
					aria-label="Next"
					className={cn(
						'calian-tables__next ',
						containerScroll > 0 &&
							containerScroll + containerWidth >= tableWidth &&
							'calian-tables__next--is-hidden',
					)}
					onClick={() => {
						scrollNext();
					}}
				>
					<span className="calian-tables__icon icon icon-caret-right-slim"></span>
				</button>
			</div>
			<div className="calian-tables__container">
				<table className="calian-tables__table">
					{headings?.length > 0 && (
						<thead>
							<tr className="calian-tables__table-heading-row">
								{headings.map(({ label }, index) => (
									<td
										className={cn(
											index === 0 &&
												'calian-tables__table-heading calian-tables__table-heading--is-first-heading',
											index != 0 &&
												'calian-tables__table-heading calian-tables__table-heading--is-not-first-heading',
										)}
										key={index}
									>
										{label}
									</td>
								))}
							</tr>
						</thead>
					)}
					{rows?.length > 0 && (
						<tbody>
							{rows.map(({ heading, columns }, index) => (
								<tr
									className="calian-tables__table-row"
									key={index}
								>
									{heading && (
										<th className="calian-tables__table-row-heading">
											{heading}
										</th>
									)}
									{columns?.length > 0 &&
										columns.map(({ items }, index2) => (
											<td
												className={cn("calian-tables__table-row-column", index2 === 0 && "calian-tables__table-row-column--highlighted")}
												key={index2}
											>
												{items?.length > 0 &&
													items.map(
														(
															{
																itemType,
																link,
																file,
																label,
															},
															index3,
														) =>
															items && (
																<React.Fragment
																	key={index3}
																>
																	{(!itemType ||
																		itemType ===
																			'text') && (
																		<div
																			className="calian-tables__label"
																			dangerouslySetInnerHTML={{
																				__html: label,
																			}}
																		/>
																	)}
																	{itemType ===
																		'empty' &&
																		items.length ===
																			1 && (
																			<div className="calian-tables__label calian-tables__empty" />
																		)}
																	{itemType ===
																		'link' && (
																		<a
																			href={
																				link?.href
																			}
																			target={
																				link?.target
																			}
																			rel={
																				link?.target
																					? 'noreferrer noopener'
																					: ''
																			}
																			className="calian-tables__link"
																		>
																			{
																				label
																			}
																		</a>
																	)}
																	{itemType ===
																		'file' && (
																		<a
																			href={
																				file?.src
																			}
																			target="_blank"
																			className="calian-tables__link"
																			rel="noreferrer noopener"
																		>
																			{
																				label
																			}
																		</a>
																	)}
																</React.Fragment>
															),
													)}
											</td>
										))}
								</tr>
							))}
						</tbody>
					)}
				</table>
			</div>
			{footnote && (
				<div
					className="calian-tables__table-footnote"
					dangerouslySetInnerHTML={{ __html: footnote }}
				/>
			)}
		</div>
	);
};

export default Tables;
